/* pz-bootstrap.js — in-house personalizer bootstrap (framework-free).

   Owns ONE abortable, idempotent lifecycle per SSR mount
   (blocks/product_personalizer-mount.liquid). It keeps Shrine's static LCP
   fast: at T0 it loads no Fabric/OpenType/release/binary; after the static
   paint it fetches ONLY the immutable release.json (never binaries), gates it as
   untrusted input, then hands off to the renderer (phase 4) via
   window.__pzBootstrap.onReady. This file NEVER hides the native gallery — the
   static Shopify media stays visible until the renderer commits an atomic first
   frame and calls markFrameCommitted().

   VALIDATION SCOPE — coarse gate ONLY. The bootstrap enforces: exact-origin
   allowlist, no URL credentials, no cross-origin credentials, post-redirect
   final-origin, decoded-body size
   cap, structural shape, and the FROZEN v1 release contract
   (schemaName/schemaVersion/identity.formatSchemaVersion). FULL schema /
   semantic / asset-integrity validation is the Phase 4 renderer boundary; this
   file does NOT claim strict schema validation.

   SECURITY (untrusted release):
   - Release URL: https, EXACT configured owned origin, NO username/password.
   - After fetch, the final (post-redirect) response origin must still be an
     exact allowlisted origin.
   - EVERY url-bearing string anywhere in the release must share the release's
     origin (recursive scan), else fail-closed.
   - Decoded-body SECURITY cap bounds the body before JSON.parse. This is NOT
     the release performance budget: the deterministic 60 KiB (61,440-byte) gzip
     budget is a compiler-side gate, not a browser wire-size check.
   - Release refusal keeps the typed render error. F1 may open marker-only native commerce
     after a bootstrap failure; renderer errors still block. It NEVER switches provider.

   LOADING MARKS (unique per page load, CAUSAL):
     pz:t0 — static shell painted (bootstrap init)
     pz:t1 — personalizer shell present; release fetch starting
     pz:t2 — selected assets/font/controls ready — ONLY via markAssetsReady()
             and ONLY when the controller exists and is READY.
     pz:t3 — atomic committed first frame — ONLY via markFrameCommitted() and
             ONLY when assetsReady (T2) already fired AND a real frame signal is
             passed.

   Exposes pure helpers for Node tests via module.exports; all DOM wiring is
   guarded behind `typeof document`. */

(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (typeof document !== 'undefined') {
    if (!root.__pzBootstrap || typeof root.__pzBootstrap._install !== 'function') root.__pzBootstrap = api;
    // Loading the generated critical bundle after a standalone bootstrap must not install a
    // hidden second controller/listener graph. The retained root instance is the one authority.
    root.__pzBootstrap._install(root, document);
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var VERSION = '1.6.6';
  var URLCtor = typeof URL === 'function' ? URL : null;
  var jsonParse = JSON.parse;
  var urlField = function (value, name) { return value[name]; };
  var arrayIsArray = Array.isArray;
  var hasOwnCall = Function.prototype.call.bind(Object.prototype.hasOwnProperty);
  var responseTextCall = null;
  var responseOkCall = null;
  var responseStatusCall = null;
  var responseUrlCall = null;
  var responseHeadersCall = null;
  var responseBodyCall = null;
  var headersGetCall = null;
  var streamGetReaderCall = null;
  var readerReadCall = null;
  var readerCancelCall = null;
  var privateTextDecoder = null;
  var textDecoderDecodeCall = null;
  var privateTextEncoder = null;
  var textEncoderEncodeCall = null;
  var privateEncodeURIComponent = encodeURIComponent;
  var PrivateUint8Array = typeof Uint8Array === 'function' ? Uint8Array : null;
  var STATE = { IDLE: 'idle', LOADING: 'loading', READY: 'ready', ERROR: 'error' };
  var PUBLIC_ERROR_CODES = Object.freeze([
    'E_ADAPTER_MISSING', 'E_CANVAS_HOST_MISSING', 'E_CRITICAL_CLASSIFIER_MISSING',
    'E_CRITICAL_CONTROLLER_MISSING', 'E_CRITICAL_RENDER_FAILED', 'E_INTERACTIVE_HOST_MISSING',
    'E_INTERACTIVE_LOAD_FAILED', 'E_INTERACTIVE_NOT_READY', 'E_INTERACTIVE_SRC_MISSING',
    'E_INTERACTIVE_TIMEOUT', 'E_NO_DOCUMENT', 'E_RELEASE_INVALID', 'E_RELEASE_MISSING',
    'E_RELEASE_VALIDATOR_MISSING', 'E_RUNTIME_SCHEMA_INCOMPATIBLE', 'E_RUNTIME_TEXTFIT_UNSUPPORTED',
    'E_ARTWORK_ASSET_MISSING', 'E_ARTWORK_BYTES_UNCAPTURED', 'E_ARTWORK_LIB_UNKNOWN',
    'E_ARTWORK_POSITION_MISSING', 'E_ARTWORK_SOURCE_AMBIGUOUS', 'E_ASSET_IDENTITY_INVALID',
    'E_ASSET_MANIFEST_MISSING', 'E_ASSET_MISSING', 'E_ASSET_ROLE_MISMATCH', 'E_CANVAS_INVALID',
    'E_COLOR_INVALID', 'E_FONT_MISSING', 'E_FRAME_LIB_UNKNOWN', 'E_FRAME_POSITION_MISSING',
    'E_FRAME_PREVIEW_ASSET_MISSING', 'E_FRAME_TIER_MISSING', 'E_GEOMETRY_NONFINITE',
    'E_LAYER_KIND_UNKNOWN', 'E_NO_INITIAL_TEMPLATE', 'E_PALETTE_COLOR_MISSING',
    'E_SCENE_ASSET_TYPE', 'E_SCENE_INVALID', 'E_STORE_BASE_MISSING', 'E_TEMPLATE_AUTHORITY',
    'E_TEMPLATE_UNKNOWN', 'E_TEXT_OPTION_AMBIGUOUS', 'E_TEXT_OPTION_UNRESOLVED',
    'E_TEXT_STYLE_MISSING', 'E_UNSUPPORTED_TEXT_FEATURE',
    'E_ASSET_DECODE', 'E_ASSET_DECODE_DIMENSIONS', 'E_ASSET_DIMENSION_BOMB', 'E_ASSET_FETCH',
    'E_ASSET_HEADER_UNREADABLE', 'E_ASSET_INTEGRITY', 'E_ASSET_MEDIA_TYPE', 'E_ASSET_PIXEL_BOMB',
    'E_ASSET_TIMEOUT', 'E_ASSET_TOO_LARGE', 'E_CANVAS_CONTEXT_UNAVAILABLE', 'E_CRYPTO_UNAVAILABLE',
    'E_FABRIC_UNAVAILABLE', 'E_FONT_IDENTITY_COLLISION', 'E_FONT_PARSER_MISSING',
    'E_FONT_SOURCE_COLLISION', 'E_FONT_VERIFICATION_REQUIRED', 'E_IMAGE_ASPECT_MISMATCH',
    'E_RENDER_MEMORY_LIMIT', 'E_SCENE_EXCEEDS_IMAGE_CACHE', 'E_SCENE_PIPELINE_MISSING',
    'E_SCENE_VALIDATOR_MISSING', 'E_TEXT_RENDERER_UNAVAILABLE'
  ]);
  var PUBLIC_ERROR_CODE_LOOKUP = Object.freeze(PUBLIC_ERROR_CODES.reduce(function (out, code) {
    out[code] = true;
    return out;
  }, {}));
  function publicErrorCode(error) {
    var code = typeof error === 'string' ? error : (error && typeof error.code === 'string' ? error.code : '');
    return /^E_[A-Z0-9_]+$/.test(code) && PUBLIC_ERROR_CODE_LOOKUP[code] === true
      ? code : 'E_CRITICAL_UNKNOWN';
  }
  var nextGeneratedSectionIdentity = 1;
  var mountIdentityCache = null;
  try {
    if (typeof WeakMap !== 'undefined') mountIdentityCache = new WeakMap();
  } catch (e) { /* the capability gate owns the no-WeakMap fail-close path */ }
  // Decoded-body SECURITY cap (bounds the parsed string), NOT the performance
  // budget. The 60 KiB gzip release budget is a compiler-side deterministic gate.
  var DEFAULT_MAX_DECODED_RELEASE_BYTES = 512 * 1024;

  // Frozen release contract from the compiler handoff. Bootstrap enforces this exact coarse
  // contract; unknown/missing values are rejected.
  //
  // schemaVersion tracks compiler/release-schema.json, which is the publishable contract
  // identity: compiler/publish-release.php refuses to publish any artifact whose
  // schemaName/schemaVersion pair differs from that file's, so exactly one version can ever
  // be in flight. It moved to 1.1.0 with content-addressed asset ids; a bootstrap still
  // pinned to 1.0.0 fail-closes every currently publishable release.
  //
  // This stays a SINGLE exact value, never a list of accepted versions — the coarse gate's
  // whole value is that an unrecognised contract cannot reach the renderer.
  // Moves in lockstep with compiler/release-schema.json. 1.2.0 adds the optional compiler-built
  // firstFrame contract and the first-frame object kind; that schema's own comment records why the
  // minor bump is mandatory — a strict 1.1 consumer rejects those properties, so a runtime that
  // understands them has to say so here or it refuses every release the compiler now emits.
  var FROZEN_RELEASE_CONTRACT = {
    schemaName: 'sc-personalizer-release',
    schemaVersion: '1.2.0',
    formatSchemaVersion: 1
  };

  // Injectable config. Nothing is trusted by default: an empty allowlist fails
  // closed. releaseContract defaults to the frozen v1 contract.
  var DEFAULT_MAX_RELEASE_SCAN_NODES = 50000;

  // F1 sale-open (owner GO 2026-09-05): a release fetch that NEVER settles must not hold the
  // native product form hostage forever. After this budget a still-LOADING controller takes the
  // same pre-frame truthful fallback as an outright fetch failure. This is a sale-preservation
  // budget, not a security or performance gate.
  var DEFAULT_RELEASE_FALLBACK_BUDGET_MS = 8000;

  var config = {
    allowedOrigins: [],
    maxDecodedReleaseBytes: DEFAULT_MAX_DECODED_RELEASE_BYTES,
    maxReleaseScanNodes: DEFAULT_MAX_RELEASE_SCAN_NODES,
    releaseContract: FROZEN_RELEASE_CONTRACT,
    // Runtime diagnostics endpoint (2026-08-26): the storefront controller posts one beacon per
    // terminal error code per page session to `<diagBase>/v1/diag`. null = beacon disabled.
    diagBase: null
  };

  // Only an https origin with an optional plain path (no query, no credentials) is a diag
  // base; anything else disables the beacon rather than pointing it somewhere odd.
  var DIAG_BASE_RE = /^https:\/\/[A-Za-z0-9.-]+(?::\d+)?(?:\/[A-Za-z0-9._~\/-]*)?$/;
  function parseDiagBase(value) {
    if (typeof value !== 'string') return null;
    var trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > 512 || !DIAG_BASE_RE.test(trimmed)) return null;
    return trimmed.replace(/\/+$/, '');
  }
  // The theme head declares ONE endpoint for every runtime on the page — the PDP mount and the
  // cart Edit modal both boot through this install, on every template — so the beacon needs no
  // per-mount attribute and no timing-sensitive configure() call.
  function readDiagBaseMeta(document) {
    try {
      var meta = document && typeof document.querySelector === 'function'
        ? document.querySelector('meta[name="pz-diag-base"]') : null;
      return meta ? parseDiagBase(meta.getAttribute('content')) : null;
    } catch (e) { return null; }
  }

  /* ---- pure helpers (Node-testable, no DOM) ---- */

  // Public section identity and internal controller identity are deliberately separate. An
  // explicit Shopify section keeps its exact existing/raw id for unload + external grouping.
  // Internal renderer calls bind to the exact controller object; DOM and generated fallbacks
  // additionally expose their typed controller id because no raw Shopify contract exists for them.
  // Thus a section id, DOM id and generated fallback with the same visible text can never
  // supersede or misroute one another. Duplicate explicit ids of the SAME type use one bounded
  // last-mount-wins slot; this is the only intentional replacement policy.
  function typedControllerIdentity(kind, identity) {
    var value = String(identity == null ? '' : identity);
    return kind + ':' + value.length + ':' + value;
  }

  function mountIdentityOf(mount) {
    if (!mount) return { sectionId: '', controllerId: '', kind: '' };
    var cached = null;
    try { if (mountIdentityCache) cached = mountIdentityCache.get(mount); } catch (e) { /* property cache below */ }
    if (!cached && !mountIdentityCache) {
      try { cached = mount.__pzCanonicalMountIdentity || null; } catch (e2) { /* resolve below */ }
    }
    if (cached && cached.sectionId && cached.controllerId && cached.kind) return cached;

    var attr = '';
    var domId = '';
    try {
      if (typeof mount.getAttribute === 'function') attr = mount.getAttribute('data-pz-section-id');
    } catch (e3) { /* fail through to DOM/generated identity */ }
    try { domId = mount.id; } catch (e4) { /* fail through to generated identity */ }
    attr = String(attr == null ? '' : attr).trim();
    domId = String(domId == null ? '' : domId).trim();

    var kind;
    var sectionId;
    if (attr) { kind = 'section'; sectionId = attr; }
    else if (domId) { kind = 'dom'; sectionId = domId; }
    else { kind = 'generated'; sectionId = 'pz-mount-' + nextGeneratedSectionIdentity++; }

    cached = {
      sectionId: sectionId,
      controllerId: typedControllerIdentity(kind, sectionId),
      kind: kind
    };
    try { if (typeof Object.freeze === 'function') Object.freeze(cached); } catch (e5) { /* best effort */ }
    try { if (mountIdentityCache) mountIdentityCache.set(mount, cached); } catch (e6) { /* property cache below */ }
    try {
      Object.defineProperty(mount, '__pzCanonicalMountIdentity', {
        value: cached,
        configurable: false,
        enumerable: false,
        writable: false
      });
    } catch (e7) {
      try { mount.__pzCanonicalMountIdentity = cached; } catch (e8) { /* WeakMap/cache result still valid */ }
    }
    return cached;
  }

  function sectionIdentityOf(mount) {
    return mountIdentityOf(mount).sectionId;
  }

  function controllerIdentityOf(mount) {
    return mountIdentityOf(mount).controllerId;
  }

  function parseAllowedOrigins(input) {
    var raw = [];
    if (arrayIsArray(input)) raw = input.slice();
    else if (typeof input === 'string') raw = input.split(',');
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var o = String(raw[i]).trim().toLowerCase().replace(/\/+$/, '');
      if (o && out.indexOf(o) === -1) out.push(o);
    }
    return out;
  }

  // scheme://host[:port], lowercased; null if unparseable.
  function originOf(url) {
    if (typeof url !== 'string' || url.length === 0) return null;
    var parsed;
    try {
      parsed = new URLCtor(url);
    } catch (e) {
      return null;
    }
    return (urlField(parsed, 'protocol') + '//' + urlField(parsed, 'host')).toLowerCase();
  }

  // https + no credentials + origin EXACTLY in the configured allowlist.
  // Empty allowlist => false.
  function isAllowedReleaseUrl(url, allowedOrigins) {
    var origins = parseAllowedOrigins(allowedOrigins);
    if (origins.length === 0) return false;
    if (typeof url !== 'string' || url.length === 0 || url.length > 2048) return false;
    var parsed;
    try {
      parsed = new URLCtor(url);
    } catch (e) {
      return false;
    }
    if (urlField(parsed, 'protocol') !== 'https:') return false;
    if (urlField(parsed, 'username') !== '' || urlField(parsed, 'password') !== '') return false; // no credentials
    var origin = (urlField(parsed, 'protocol') + '//' + urlField(parsed, 'host')).toLowerCase();
    return origins.indexOf(origin) !== -1;
  }

  // Iteratively collect every url-bearing string (contains '://') anywhere in
  // the release graph. WeakSet cycle guard (O(1)), explicit-stack (no recursion),
  // and a hard node cap. Returns { urls, complete }: complete=false when the cap
  // is hit, so the caller can fail-closed instead of trusting a partial scan.
  // This is an ORIGIN gate, not a schema validator.
  function collectAllUrls(release, maxNodes) {
    var cap = maxNodes || config.maxReleaseScanNodes || DEFAULT_MAX_RELEASE_SCAN_NODES;
    var urls = [];
    // Object-identity set: WeakSet when present, else Set (a required capability).
    // Both are O(1) has/add — never the O(n^2) array scan, which would be slow
    // over tens of thousands of nodes on exactly the old devices we support.
    var seen = (typeof WeakSet !== 'undefined') ? new WeakSet() : new Set();
    var stack = [release];
    var count = 0;
    var complete = true;
    while (stack.length) {
      if (count++ >= cap) { complete = false; break; }
      var node = stack.pop();
      if (node == null) continue;
      var t = typeof node;
      if (t === 'string') { if (node.indexOf('://') !== -1) urls.push(node); continue; }
      if (t !== 'object') continue;
      if (seen.has(node)) continue;
      seen.add(node);
      if (arrayIsArray(node)) {
        for (var i = 0; i < node.length; i++) stack.push(node[i]);
      } else {
        for (var k in node) { if (hasOwnCall(node, k)) stack.push(node[k]); }
      }
    }
    return { urls: urls, complete: complete };
  }

  // Every url-bearing string in the release must share the release URL's exact
  // origin. Fails closed if the traversal cap was exceeded (cannot verify all).
  function assetsSameOrigin(release, releaseUrl) {
    var base = originOf(releaseUrl);
    if (!base) return false;
    var scan = collectAllUrls(release);
    if (!scan.complete) return false; // partial scan -> cannot prove same-origin
    for (var i = 0; i < scan.urls.length; i++) {
      if (originOf(scan.urls[i]) !== base) return false;
    }
    return true;
  }

  function isValidReleaseShape(release) {
    if (!release || typeof release !== 'object' || arrayIsArray(release)) return false;
    var hasIdentity = hasOwnCall(release, 'identity') && release.identity && typeof release.identity === 'object';
    var hasTemplates = hasOwnCall(release, 'templates')
      && (arrayIsArray(release.templates) || (release.templates && typeof release.templates === 'object'));
    return !!(hasIdentity && hasTemplates);
  }

  // Exact frozen-contract match. Unknown/missing schemaName, schemaVersion, or
  // identity.formatSchemaVersion are rejected.
  function matchesReleaseContract(release, contract) {
    var c = contract || FROZEN_RELEASE_CONTRACT;
    if (!isValidReleaseShape(release)) return false;
    if (!hasOwnCall(release, 'schemaName') || release.schemaName !== c.schemaName) return false;
    if (!hasOwnCall(release, 'schemaVersion') || release.schemaVersion !== c.schemaVersion) return false;
    if (!release.identity || !hasOwnCall(release.identity, 'formatSchemaVersion')
      || release.identity.formatSchemaVersion !== c.formatSchemaVersion) return false;
    return true;
  }

  // ---- causal-mark predicates ----
  function canMarkAssetsReady(controller) {
    return !!(controller && controller.state === STATE.READY);
  }
  function canMarkFrameCommitted(controller, frameSignal) {
    return !!(controller && controller.assetsReady === true && frameSignal);
  }

  // ---- aria token-list helpers (pure) ----
  // PZ appends/removes ONLY its own token, preserving every pre-existing or
  // dynamically-added token from Shopify/other apps.
  function addAriaToken(value, token) {
    var list = String(value == null ? '' : value).split(/\s+/).filter(Boolean);
    if (list.indexOf(token) === -1) list.push(token);
    return list.join(' ');
  }
  function removeAriaToken(value, token) {
    var list = String(value == null ? '' : value).split(/\s+/).filter(Boolean);
    var out = [];
    for (var i = 0; i < list.length; i++) { if (list[i] !== token) out.push(list[i]); }
    return out.join(' ');
  }

  // ---- ATC block invariant (pure) ----
  // Block if ANY inhouse controller is in error OR has not committed a frame.
  // No controllers (e.g. a non-inhouse page) => never block.
  function atcShouldBlock(controllerStates) {
    if (!controllerStates || !controllerStates.length) return false;
    for (var i = 0; i < controllerStates.length; i++) {
      var c = controllerStates[i];
      // A committed frame opens commerce; the explicit transport fallback (weak device / slow
      // network, owner 02/09) and owned bootstrap fallback are the no-frame exceptions.
      // Bootstrap can retain its render ERROR while opening marker-only commerce; a generic
      // renderer ERROR (including the READY M1 path) still blocks.
      if (c.bootstrapFallback === true && c.commerceFallback === true) continue;
      if (c.state === STATE.ERROR || (!c.frameCommitted && c.commerceFallback !== true)) return true;
    }
    return false;
  }

  function byteLength(text) {
    if (privateTextEncoder && textEncoderEncodeCall) {
      return textEncoderEncodeCall(new privateTextEncoder(), text).length;
    }
    var encoded = privateEncodeURIComponent(text); var length = 0;
    for (var i = 0; i < encoded.length; i++) { length += 1; if (encoded[i] === '%') i += 2; }
    return length;
  }

  function safeHost(url) {
    try { return urlField(new URLCtor(url), 'host'); } catch (e) { return '(invalid-url)'; }
  }

  function tooLargeError() {
    var e = new Error('release-too-large');
    e.code = 'release-too-large';
    return e;
  }

  function decodeBytes(chunks, total) {
    var buf = new PrivateUint8Array(total);
    var off = 0;
    for (var i = 0; i < chunks.length; i++) { buf.set(chunks[i], off); off += chunks[i].byteLength; }
    var decoder = new privateTextDecoder('utf-8');
    return textDecoderDecodeCall(decoder, buf);
  }

  // Read a response body without allocating past the decoded-body security cap.
  // Modern browsers: incrementally read the stream and ABORT once received bytes
  // exceed the cap (never buffers a malicious multi-GB body). Legacy fallback
  // (no ReadableStream/TextDecoder): a Content-Length > cap precheck, then the
  // decoded cap. Residual: an absent/lying Content-Length falls back to reading
  // the decoded body — acceptable ONLY because the origin is the exact
  // allowlisted owned origin (trusted infra).
  // Tag only rejections thrown by the native transport call, never decode/parse/policy code.
  // WebKit and Chromium use different messages (including mid-body TypeError); words are not policy.
  function releaseTransport(call, phase) {
    function failed(error) {
      if (error && (error.name === 'AbortError' || typeof error.code === 'string')) throw error;
      var loss = new Error('release-fetch-failed');
      loss.releaseTransport = true;
      loss.releasePhase = phase;
      throw loss;
    }
    try { return call().then(null, failed); }
    catch (error) { return Promise.resolve().then(function () { return failed(error); }); }
  }

  function readCapped(res, maxBytes) {
    var body = responseBodyCall && responseBodyCall(res);
    if (body && streamGetReaderCall && readerReadCall && readerCancelCall
      && privateTextDecoder && textDecoderDecodeCall && PrivateUint8Array) {
      var reader = streamGetReaderCall(body);
      var received = 0;
      var chunks = [];
      var pump = function () {
        return releaseTransport(function () { return readerReadCall(reader); }, 'body').then(function (r) {
          if (r.done) return decodeBytes(chunks, received);
          var len = r.value ? r.value.byteLength : 0;
          received += len;
          if (received > maxBytes) { try { readerCancelCall(reader); } catch (e) {} throw tooLargeError(); }
          chunks.push(r.value);
          return pump();
        });
      };
      return pump();
    }
    var responseHeaders = responseHeadersCall && responseHeadersCall(res);
    var cl = responseHeaders && headersGetCall && headersGetCall(responseHeaders, 'content-length');
    if (cl && parseInt(cl, 10) > maxBytes) return Promise.reject(tooLargeError());
    return releaseTransport(function () { return responseTextCall(res); }, 'body').then(function (text) {
      if (byteLength(text) > maxBytes) throw tooLargeError();
      return text;
    });
  }

  // Every ATC entrypoint selector, shared by the normal path and the degraded
  // fail-close path.
  var ATC_SELECTOR = 'form[action^="/cart/add"] [name="add"], .product-form__submit, .sticky-atc__add_button, button[name="add"]';

  // Frozen minimum browser capability = ONLY primitives with no safe fallback,
  // whose construction-time absence would otherwise throw and leave ATC enabled:
  //   Promise, fetch, URL, Map, Set, WeakMap, MutationObserver.
  // Intentionally NOT required (each already degrades safely, so requiring them
  // would needlessly fail-close older browsers):
  //   AbortController -> fetch runs with no signal + controller.disposed guard.
  //   WeakSet         -> collectAllUrls falls back to a Set (O(1), still required).
  //   TextDecoder     -> readCapped falls back to res.text() + byte cap.
  //   TextEncoder     -> byteLength falls back to encodeURIComponent counting.
  var REQUIRED_CAPABILITIES = ['Promise', 'fetch', 'URL', 'Map', 'Set', 'WeakMap', 'MutationObserver'];

  // First required capability missing from env, or null. Never throws.
  function firstMissingCapability(env) {
    if (!env) return REQUIRED_CAPABILITIES[0];
    for (var i = 0; i < REQUIRED_CAPABILITIES.length; i++) {
      var name = REQUIRED_CAPABILITIES[i];
      try { if (typeof env[name] === 'undefined' || env[name] == null) return name; }
      catch (e) { return name; }
    }
    return null;
  }

  // Guarded AbortController construction — NEVER throws when the constructor is
  // absent or fails; the caller degrades to signal: undefined.
  function makeAbortHandle(AbortCtor) {
    if (typeof AbortCtor === 'function') {
      try { var c = new AbortCtor(); return { controller: c, signal: c.signal }; }
      catch (e) { /* fall through */ }
    }
    return { controller: null, signal: undefined };
  }

  // DOM-only hard fail-close (no Map/Set/MutationObserver) for a missing REQUIRED
  // capability. Disables present ATC AND installs capture-phase submit+click
  // guards, so a keyboard/programmatic submit or a late-inserted/JS-driven CTA
  // (which no observer can catch in the degraded path) is still blocked. Only
  // acts on pages that have an inhouse mount. Idempotent per install.
  function installCapabilityFailClose(root, document, missing) {
    function addClass(el, cls) { if (el && el.className != null && String(el.className).indexOf(cls) === -1) el.className += ' ' + cls; }
    function hasInhouseMount() { return !!document.querySelector('[data-pz-personalizer]'); }
    function hardFailClose() {
      var mounts = document.querySelectorAll('[data-pz-personalizer]');
      if (!mounts.length) return;
      var i;
      for (i = 0; i < mounts.length; i++) { mounts[i].setAttribute('data-pz-state', 'error'); addClass(mounts[i], 'pz-mount--error'); }
      var atc = document.querySelectorAll(ATC_SELECTOR);
      for (i = 0; i < atc.length; i++) {
        atc[i].setAttribute('aria-disabled', 'true');
        if ('disabled' in atc[i]) atc[i].disabled = true;
        addClass(atc[i], 'pz-atc-blocked');
      }
      addClass(document.body, 'pz-atc-blocked-page');
      addClass(document.body, 'pz-atc-error-page');
      // Code only (owner 10/10): never ids, detail objects or shopper values on the web console.
      try { console.error('[pz-bootstrap] capability-missing-fail-closed'); } catch (e) {}
    }
    document.addEventListener('submit', function (e) {
      if (hasInhouseMount() && e.target && typeof e.target.matches === 'function' &&
          e.target.matches('form[action^="/cart/add"]')) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (hasInhouseMount() && t && typeof t.closest === 'function' && t.closest(ATC_SELECTOR)) {
        e.preventDefault(); e.stopImmediatePropagation();
      }
    }, true);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hardFailClose);
    else hardFailClose();
    try { document.addEventListener('shopify:section:load', hardFailClose); } catch (e) {}
  }

  var api = {
    version: VERSION,
    STATE: STATE,
    PUBLIC_ERROR_CODES: PUBLIC_ERROR_CODES,
    publicErrorCode: publicErrorCode,
    FROZEN_RELEASE_CONTRACT: FROZEN_RELEASE_CONTRACT,
    REQUIRED_CAPABILITIES: REQUIRED_CAPABILITIES,
    firstMissingCapability: firstMissingCapability,
    sectionIdentityOf: sectionIdentityOf,
    controllerIdentityOf: controllerIdentityOf,
    makeAbortHandle: makeAbortHandle,
    parseAllowedOrigins: parseAllowedOrigins,
    originOf: originOf,
    isAllowedReleaseUrl: isAllowedReleaseUrl,
    collectAllUrls: collectAllUrls,
    assetsSameOrigin: assetsSameOrigin,
    isValidReleaseShape: isValidReleaseShape,
    matchesReleaseContract: matchesReleaseContract,
    canMarkAssetsReady: canMarkAssetsReady,
    canMarkFrameCommitted: canMarkFrameCommitted,
    atcShouldBlock: atcShouldBlock,
    addAriaToken: addAriaToken,
    removeAriaToken: removeAriaToken,
    byteLength: byteLength,
    safeHost: safeHost,
    configure: function (next) {
      if (!next) return config;
      if (next.allowedOrigins != null) config.allowedOrigins = parseAllowedOrigins(next.allowedOrigins);
      if (typeof next.maxDecodedReleaseBytes === 'number' && next.maxDecodedReleaseBytes > 0) config.maxDecodedReleaseBytes = next.maxDecodedReleaseBytes;
      if (typeof next.maxReleaseScanNodes === 'number' && next.maxReleaseScanNodes > 0) config.maxReleaseScanNodes = next.maxReleaseScanNodes;
      if (next.releaseContract && typeof next.releaseContract === 'object') config.releaseContract = next.releaseContract;
      if (next.diagBase !== undefined) config.diagBase = parseDiagBase(next.diagBase);
      return config;
    },
    parseDiagBase: parseDiagBase,
    getConfig: function () { return config; }
  };

  /* ---- DOM install (guarded) ---- */

  api._install = function (root, document) {
    if (api._installed) return;
    api._installed = true;
    if (config.diagBase == null) config.diagBase = readDiagBaseMeta(document);

    // Capability gate BEFORE any construction-time API (Map/Set/WeakMap/
    // MutationObserver). A missing REQUIRED capability fails CLOSED via a
    // DOM-only path; it must never crash or leave ATC enabled on an inhouse PDP.
    var missing = firstMissingCapability(root);
    if (missing) {
      api._degraded = missing;
      installCapabilityFailClose(root, document, missing);
      return;
    }
    var nativeFetch;
    try {
      var cleanFrame = document.createElement('iframe');
      cleanFrame.hidden = true; cleanFrame.tabIndex = -1; cleanFrame.style.display = 'none';
      document.documentElement.appendChild(cleanFrame);
      var clean = cleanFrame.contentWindow;
      if (!clean || typeof clean.fetch !== 'function' || typeof clean.URL !== 'function'
        || !clean.JSON || !clean.Response || !clean.Headers || !clean.Object || !clean.Array
        || !clean.Function) throw new Error('clean-authority-realm');
      function bindCall(fn) { return clean.Function.prototype.call.bind(fn); }
      var cleanGetOwn = clean.Object.getOwnPropertyDescriptor;
      var cleanGetProto = clean.Object.getPrototypeOf;
      function getter(proto, name) {
        var cursor = proto;
        while (cursor) {
          var descriptor = cleanGetOwn(cursor, name);
          if (descriptor && typeof descriptor.get === 'function') return bindCall(descriptor.get);
          cursor = cleanGetProto(cursor);
        }
        return null;
      }
      var URLProto = clean.URL.prototype;
      var urlGetters = {};
      var urlNames = ['protocol', 'host', 'username', 'password'];
      for (var u = 0; u < urlNames.length; u++) urlGetters[urlNames[u]] = getter(URLProto, urlNames[u]);
      if (!urlGetters.protocol || !urlGetters.host || !urlGetters.username || !urlGetters.password) {
        throw new Error('clean-url-authority');
      }
      URLCtor = clean.URL;
      urlField = function (value, name) {
        var read = urlGetters[name];
        if (!read) throw new Error('clean-url-field');
        return read(value);
      };
      var cleanParse = bindCall(clean.JSON.parse);
      var cleanIsArray = clean.Array.isArray;
      var cleanKeys = clean.Object.keys;
      var cleanDefine = clean.Object.defineProperty;
      arrayIsArray = cleanIsArray;
      hasOwnCall = bindCall(clean.Object.prototype.hasOwnProperty);
      function localJson(value, depth, count) {
        if (++count.n > 50000 || depth > 64) throw new Error('release-json-limit');
        var type = typeof value;
        if (value === null || type === 'string' || type === 'boolean' || type === 'number') return value;
        if (type !== 'object') throw new Error('release-json-type');
        var array = cleanIsArray(value); var output = array ? [] : {}; var keys = cleanKeys(value);
        for (var j = 0; j < keys.length; j++) {
          var key = keys[j]; var child = localJson(value[key], depth + 1, count);
          if (array) output[key] = child;
          else cleanDefine(output, key, { value: child, enumerable: true, writable: true, configurable: true });
        }
        return output;
      }
      jsonParse = function (text) { return localJson(cleanParse(clean.JSON, text), 0, { n: 0 }); };
      nativeFetch = clean.fetch.bind(clean);
      var ResponseProto = clean.Response.prototype;
      responseTextCall = bindCall(ResponseProto.text);
      responseOkCall = getter(ResponseProto, 'ok');
      responseStatusCall = getter(ResponseProto, 'status');
      responseUrlCall = getter(ResponseProto, 'url');
      responseHeadersCall = getter(ResponseProto, 'headers');
      responseBodyCall = getter(ResponseProto, 'body');
      headersGetCall = bindCall(clean.Headers.prototype.get);
      if (clean.ReadableStream && clean.ReadableStream.prototype
        && clean.ReadableStreamDefaultReader && clean.ReadableStreamDefaultReader.prototype) {
        streamGetReaderCall = bindCall(clean.ReadableStream.prototype.getReader);
        readerReadCall = bindCall(clean.ReadableStreamDefaultReader.prototype.read);
        readerCancelCall = bindCall(clean.ReadableStreamDefaultReader.prototype.cancel);
      }
      privateTextDecoder = typeof clean.TextDecoder === 'function' ? clean.TextDecoder : null;
      textDecoderDecodeCall = privateTextDecoder && bindCall(privateTextDecoder.prototype.decode);
      privateTextEncoder = typeof clean.TextEncoder === 'function' ? clean.TextEncoder : null;
      textEncoderEncodeCall = privateTextEncoder && bindCall(privateTextEncoder.prototype.encode);
      if (typeof clean.encodeURIComponent === 'function') privateEncodeURIComponent = clean.encodeURIComponent;
      PrivateUint8Array = clean.Uint8Array || PrivateUint8Array;
      if (!responseTextCall || !responseOkCall || !responseStatusCall || !responseUrlCall
        || !responseHeadersCall || !headersGetCall) throw new Error('clean-response-authority');
    } catch (authorityError) {
      api._degraded = 'clean-authority-realm';
      installCapabilityFailClose(root, document, api._degraded);
      return;
    }

    var performance = root.performance || { mark: function () {}, now: function () { return 0; } };
    var controllers = new Map(); // typed controllerId -> committed controller
    // One exact reservation owns each controllerId while it is initializing and for the
    // lifetime of the committed controller. A re-entrant refresh replaces the reservation
    // before the stale outer stack can commit, so only the latest generation can fetch/ready.
    // The entry is removed with lifecycle ownership; it is not a historical generation counter.
    var reservations = new Map(); // typed controllerId -> opaque { controller }
    var emittedMarks = Object.create(null); // page-scoped unique marks
    var readyCallbacks = [];
    var failCallbacks = []; // F1 sale-open: pre-frame failure consumers (see fireFail/onFail)
    var recoveryFieldOwners = new WeakMap(); // input -> exact lifecycle write record

    function mark(name) {
      if (emittedMarks[name]) return;
      emittedMarks[name] = true;
      try { performance.mark(name); } catch (e) { /* no-op */ }
    }

    // Code only (owner 10/10): `extra` stays server-side; the web console never carries detail objects.
    function log(code) {
      try { console.error('[pz-bootstrap] ' + String(code == null ? '' : code).slice(0, 80)); } catch (e) { /* no-op */ }
    }

    function atcEntrypoints() {
      return document.querySelectorAll(ATC_SELECTOR);
    }

    var atcBlocked = false;

    // SOFT gate: PZ owns ONLY PZ-namespaced state — never the native `disabled`
    // property and never the native `aria-disabled` attribute. Owning either lets
    // a block-time value go stale when Shopify/another app marks the button
    // sold-out mid-gate, and clearing it at T3 would erase that native semantics.
    // Purchase is prevented by the capture-phase submit/click guards. The visual
    // CSS belt deliberately leaves pointer events reachable so a blocked click can
    // explain the gate instead of becoming a silent dead button. Accessibility is
    // conveyed by appending ONE
    // PZ-owned aria-describedby token pointing at a hidden live status, plus
    // aria-busy on the cart form (only removed if PZ set it). Native availability
    // (static sold-out, or a dynamic available->sold-out / aria-disabled applied
    // during the gate) is always authoritative and untouched by PZ.
    var ARIA_STATUS_ID = 'pz-atc-status';

    function ensureStatusEl() {
      var el = document.getElementById(ARIA_STATUS_ID);
      if (!el && document.body) {
        el = document.createElement('span');
        el.id = ARIA_STATUS_ID;
        el.setAttribute('role', 'status');
        el.className = 'pz-visually-hidden';
        el.textContent = 'Personalization is loading. Add to cart is unavailable until your design is ready.';
        document.body.appendChild(el);
      }
      return el;
    }

    // One shopper intent, only while the release is loading. Never queue validation errors.
    var loadingIntent = null;
    function clearLoadingIntent(message) {
      var intent = loadingIntent;
      loadingIntent = null;
      if (!intent || !intent.notice) return;
      if (message) intent.notice.textContent = message;
      else if (intent.notice.parentNode) intent.notice.parentNode.removeChild(intent.notice);
    }
    function queueLoadingPurchase(atc, submitter) {
      var form = atc && (atc.form || (atc.matches && atc.matches('form') ? atc : null));
      if (!form && atc && atc.closest) form = atc.closest('form[action^="/cart/add"]');
      var owner = null;
      var count = 0;
      controllers.forEach(function (c) {
        if (form && nativeFormFor(c) !== form) return;
        owner = c; count++;
      });
      if (count !== 1 || !owner || !ownsLifecycle(owner)) return false;
      var initialFrameLoading = owner.state === STATE.READY && !owner.visualFrameCommitted
        && !owner.mount.classList.contains('pz-has-committed-frame');
      if (owner.state !== STATE.LOADING && !initialFrameLoading) return false;
      // A sticky delegate without a form is safe only with exactly one native product form.
      form = form || nativeFormFor(owner);
      if (!form || !atc || atc.disabled || atc.getAttribute('aria-disabled') === 'true'
        || (submitter && (submitter.disabled || submitter.getAttribute('aria-disabled') === 'true'))) return false;
      if (loadingIntent) return true; // repeated taps do not make another intent
      var notice = document.createElement('span');
      notice.className = 'pz-atc-loading-notice';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.textContent = 'Loading your design . . .';
      if (!atc.parentNode) return false;
      atc.parentNode.insertBefore(notice, atc.nextSibling);
      loadingIntent = { controller: owner, atc: atc, form: form, boundForm: atc.form || null, submitter: submitter || null,
        variant: ownedShopifyVariantId(owner), notice: notice };
      return true;
    }
    function resumeLoadingPurchase() {
      var intent = loadingIntent;
      if (!intent) return;
      var c = intent.controller;
      if (!ownsLifecycle(c) || !document.contains(c.mount) || !document.contains(intent.atc)
        || nativeFormFor(c) !== intent.form || ownedShopifyVariantId(c) !== intent.variant
        || c.state === STATE.ERROR || c.bootstrapFallback) {
        clearLoadingIntent('Please check your design, then tap Add to cart.');
        return;
      }
      if (c.state !== STATE.READY) return;
      if (!c.visualFrameCommitted && !c.frameCommitted) return;
      if (typeof c.purchaseValidator !== 'function') return;
      var valid = false;
      try { valid = c.purchaseValidator() !== false; } catch (e) { /* fail closed */ }
      if (!valid) {
        clearLoadingIntent('Please complete the highlighted personalization options, then tap Add to cart.');
        return;
      }
      if (atcBlocked) return;
      // Cross only a microtask, so the frame callback can finish binding normal commerce first.
      root.Promise.resolve().then(function () {
        if (loadingIntent !== intent || atcBlocked) return;
        if (!ownsLifecycle(c) || !document.contains(intent.atc) || nativeFormFor(c) !== intent.form
          || ownedShopifyVariantId(c) !== intent.variant || intent.atc.disabled
          || (intent.atc.form || null) !== intent.boundForm
          || intent.atc.getAttribute('aria-disabled') === 'true'
          || (intent.submitter && (intent.submitter.disabled || intent.submitter.form !== intent.form
            || intent.submitter.getAttribute('aria-disabled') === 'true'))) {
          clearLoadingIntent('Please check your selection, then tap Add to cart.');
          return;
        }
        clearLoadingIntent(); // clear BEFORE dispatch: no recursive/repeated add
        if (intent.atc === intent.form) {
          if (typeof intent.form.requestSubmit === 'function') intent.form.requestSubmit(intent.submitter || undefined);
        } else if (typeof intent.atc.click === 'function') intent.atc.click();
      });
    }
    root.addEventListener('pagehide', function () { clearLoadingIntent(); });

    function purchaseValidatorFor(atc) {
      var formId = '';
      if (atc && typeof atc.getAttribute === 'function') formId = String(atc.getAttribute('form') || '').trim();
      if (!formId && atc && typeof atc.closest === 'function') {
        var form = atc.closest('form[action^="/cart/add"]');
        // Shopify product forms own an input[name="id"]. HTMLFormElement named-property access
        // can therefore make form.id resolve to that INPUT instead of the element id string.
        formId = form && typeof form.getAttribute === 'function'
          ? String(form.getAttribute('id') || '').trim() : '';
      }
      var found = null;
      var count = 0;
      controllers.forEach(function (controller) {
        if (controller.state !== STATE.READY || typeof controller.purchaseValidator !== 'function') return;
        var ownedFormId = controller.mount && controller.mount.getAttribute
          ? String(controller.mount.getAttribute('data-pz-form-id') || '').trim() : '';
        if (formId && ownedFormId !== formId) return;
        found = controller.purchaseValidator;
        count++;
      });
      return count === 1 ? found : null;
    }

    function explainBlockedPurchase(atc) {
      var status = ensureStatusEl();
      var hasError = false;
      controllers.forEach(function (controller) {
        if (controller.state === STATE.ERROR) hasError = true;
      });
      // A missing-glyph refusal is recoverable input, not a broken design: the runtime marks it
      // with data-pz-runtime-error-kind="glyph" so we describe the real cause and the fix (edit
      // the name) instead of telling the shopper to reload a design that actually loaded fine.
      // A glyph refusal is RECOVERABLE and no longer reaches STATE.ERROR (the runtime re-blocks
      // through markFramePending so T2/T3 can re-arm after the shopper edits the character), so
      // the kind marker alone decides this copy - it must not be gated on hasError.
      var glyphError = document.querySelector('[data-pz-runtime-error-kind="glyph"]') !== null; // 6s-15 cdn-flush
      var incomplete = false;
      var validator = purchaseValidatorFor(atc);
      if (validator) {
        try { incomplete = validator() === false; }
        catch (e) { log('purchase-validator-failed', {}); }
      }
      if (status) {
        status.textContent = glyphError
          ? 'Please use English letters only - no emojis. Fix the name and Add to cart will unlock — no need to reload.'
          : hasError
            ? 'Your design could not load. Please refresh the page before adding this item to cart.'
            : incomplete
              ? 'Please complete the highlighted personalization options before adding this item to cart.'
              : 'Please wait for your design to finish loading, then complete the required personalization options.';
      }

      // The runtime validator owns required-field errors and first-invalid focus. Do not
      // immediately steal focus back to the first control after it has handled an incomplete
      // design. The generic fallback below is only for a fully completed design whose latest
      // frame is still pending, or when no exact validator is installed.
      if (incomplete) return;

      // Prefer the exact first enabled control owned by an in-house section. Do not
      // synthesize validation or mutate native form availability here; the runtime
      // performs field-level validation once the committed frame releases the gate.
      var controls = document.querySelector('[data-pz-controls-for]');
      var target = controls && controls.querySelector
        ? controls.querySelector([
          '[data-pz-model-trigger]:not([disabled])',
          '[data-pz-model-option]:not([disabled])',
          'input:not([disabled]):not([type="hidden"])',
          'textarea:not([disabled])',
          'select:not([disabled]):not(.pz-model-combobox__native)',
          'button:not([disabled])'
        ].join(', '))
        : null;
      if (target && typeof target.focus === 'function') {
        try { target.focus({ preventScroll: false }); } catch (e) { try { target.focus(); } catch (e2) { /* no-op */ } }
      }
    }

    function applyAtcGate(el) {
      el.classList.add('pz-atc-blocked');
      el.setAttribute('data-pz-gated', '');
      el.setAttribute('aria-describedby', addAriaToken(el.getAttribute('aria-describedby'), ARIA_STATUS_ID));
    }
    function clearAtcGate(el) {
      el.classList.remove('pz-atc-blocked');
      el.removeAttribute('data-pz-gated');
      var next = removeAriaToken(el.getAttribute('aria-describedby'), ARIA_STATUS_ID);
      if (next) el.setAttribute('aria-describedby', next);
      else el.removeAttribute('aria-describedby');
    }
    function gateAll(apply) {
      var nodes = atcEntrypoints();
      for (var i = 0; i < nodes.length; i++) (apply ? applyAtcGate : clearAtcGate)(nodes[i]);
    }

    // aria-busy on the cart form, with an ownership marker so PZ only removes
    // what PZ set (never clears an aria-busy another app owns).
    function setFormBusy(on) {
      var forms = document.querySelectorAll('form[action^="/cart/add"]');
      for (var i = 0; i < forms.length; i++) {
        var f = forms[i];
        if (on) {
          if (f.getAttribute('aria-busy') !== 'true') {
            f.setAttribute('aria-busy', 'true');
            f.setAttribute('data-pz-aria-busy', '');
          }
        } else if (f.hasAttribute('data-pz-aria-busy')) {
          f.removeAttribute('aria-busy');
          f.removeAttribute('data-pz-aria-busy');
        }
      }
    }

    function setAtcBlocked(blocked, hasError) {
      atcBlocked = blocked;
      if (blocked) ensureStatusEl();
      gateAll(blocked);
      setFormBusy(blocked);
      if (blocked) startAtcObserver(); else stopAtcObserver();
      if (document.body) {
        document.body.classList.toggle('pz-atc-blocked-page', blocked);
        document.body.classList.toggle('pz-atc-error-page', blocked && hasError === true);
      }
    }

    // Every inhouse controller blocks ATC from init/LOADING through READY and T2;
    // it unblocks after a real committed frame (controller.frameCommitted,
    // set by markFrameCommitted on a T3 signal) or an explicit truthful commerce fallback. Recomputed
    // on init/error/ready/T2/T3/pending/teardown/bfcache. If the runtime never
    // commits a frame, ATC stays permanently safe (fail-closed).
    function recomputeAtc() {
      var states = [];
      var hasError = false;
      controllers.forEach(function (c) {
        states.push({ state: c.state, frameCommitted: c.frameCommitted, commerceFallback: c.commerceFallback, bootstrapFallback: c.bootstrapFallback });
        if (c.state === STATE.ERROR) hasError = true;
      });
      setAtcBlocked(atcShouldBlock(states), hasError);
      resumeLoadingPurchase();
    }

    // Block form submits while fail-closed.
    document.addEventListener('submit', function (e) {
      if (e.target && typeof e.target.matches === 'function' &&
          e.target.matches('form[action^="/cart/add"]')) {
        // Shopify may replace the native form without a section-load event. Move the existing
        // fallback write-set before handing off this submit, not on a later refresh/click.
        retryBootstrapFailures();
        if (!atcBlocked) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!queueLoadingPurchase(e.target, e.submitter)) explainBlockedPurchase(e.target);
      }
    }, true);

    // Block direct clicks on any ATC entrypoint while fail-closed — covers
    // JS-driven cart calls that never fire a form submit.
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && typeof t.closest === 'function' && t.closest(ATC_SELECTOR)) {
        retryBootstrapFailures(); // click-driven cart capture may never emit submit
        if (!atcBlocked) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!queueLoadingPurchase(t.closest(ATC_SELECTOR))) explainBlockedPurchase(t.closest(ATC_SELECTOR));
      }
    }, true);

    // Late-inserted ATC controls (sticky ATC, re-rendered forms) must also be
    // disabled while blocked. The observer is connected ONLY while blocked and
    // touches ONLY newly-added matching nodes (no full rescan) — cheap on
    // low-end phones. It disconnects on unblock/teardown.
    function blockNewNodes(records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (!n || n.nodeType !== 1) continue; // elements only
          if (typeof n.matches === 'function' && n.matches(ATC_SELECTOR)) applyAtcGate(n);
          if (typeof n.querySelectorAll === 'function') {
            var inner = n.querySelectorAll(ATC_SELECTOR);
            for (var k = 0; k < inner.length; k++) applyAtcGate(inner[k]);
          }
        }
      }
    }
    // A failed mount can precede its native form (late bundle / section insertion). Reuse the
    // blocked-node observer, not a polling timer or a second initialization lifecycle.
    var atcObserver = new MutationObserver(function (records) {
      blockNewNodes(records);
      retryBootstrapFailures();
    });
    var atcObserving = false;
    function startAtcObserver() {
      if (!atcObserving && document.body) { atcObserver.observe(document.body, { childList: true, subtree: true }); atcObserving = true; }
    }
    function stopAtcObserver() {
      if (atcObserving) { atcObserver.disconnect(); atcObserving = false; }
    }
    api._atcObserving = function () { return atcObserving; };

    function setState(controller, state) {
      controller.state = state;
      if (controller.mount) controller.mount.setAttribute('data-pz-state', state);
    }

    function ownsLifecycle(controller) {
      return !!(controller && !controller.disposed &&
        controllers.get(controller.controllerId) === controller &&
        reservations.get(controller.controllerId) === controller.reservation);
    }

    // Only pre-release reasons owned by this bootstrap may enter the observer's load message.
    function preReleaseFailReason(code) {
      switch (code) {
        case 'inhouse-invalid-metadata':
        case 'invalid-release-url':
        case 'release-timeout':
        case 'release-timeout-hidden':
        case 'release-timeout-late':
        case 'release-http-error':
        case 'release-redirect-origin':
        case 'release-parse-error':
        case 'release-contract-mismatch':
        case 'release-asset-origin':
        case 'release-too-large':
        case 'release-fetch-failed':
          return code;
        default: return 'unknown';
      }
    }
    function failClosed(controller, code, extra, runtimeReason) {
      clearFallbackTimer(controller);
      clearBootstrapFallback(controller);
      if (controller.mount) controller.mount.setAttribute('data-pz-fail-reason', runtimeReason || preReleaseFailReason(code));
      setState(controller, STATE.ERROR);
      controller.commerceFallback = false;
      if (controller.mount) controller.mount.classList.add('pz-mount--error');
      recomputeAtc();
      log(code, extra);
    }

    // ---- F1 sale-open: pre-frame truthful commerce fallback (owner GO 2026-09-05) ------------
    // Renderer still refuses an unusable release; purchase is separated from render. Once the
    // native product form is ready, a pre-frame bootstrap failure opens commerce on a truthful
    // marker-only recovery line instead of hard-blocking every Add to Cart.

    function clearFallbackTimer(controller) {
      if (controller && controller.fallbackTimer != null && typeof root.clearTimeout === 'function') {
        try { root.clearTimeout(controller.fallbackTimer); } catch (e) { /* noop */ }
      }
      if (controller) controller.fallbackTimer = null;
    }

    // The controller's native Shopify add-to-cart form, or null. The recovery line is added
    // through exactly this form, so the fallback stays closed until it exists in the DOM.
    function nativeFormFor(controller) {
      var mount = controller && controller.mount;
      if (!mount || typeof document.getElementById !== 'function' || !mount.getAttribute) return null;
      var formId = String(mount.getAttribute('data-pz-form-id') || '').trim();
      var form = formId ? document.getElementById(formId) : null;
      return form && typeof form.appendChild === 'function' ? form : null;
    }

    // Map a coarse bootstrap failure code to an allowlisted public diag code for the mount
    // attribute (the exact dashed reason still goes to the console log). Both are in
    // PUBLIC_ERROR_CODES so the runtime<->theme allowlist parity holds without a new code.
    function fallbackDiagCode(code) {
      switch (code) {
        case 'inhouse-invalid-metadata':
        case 'release-parse-error':
        case 'release-contract-mismatch':
        case 'release-asset-origin':
        case 'release-too-large':
          return 'E_RELEASE_INVALID';
        default:
          return 'E_RELEASE_MISSING';
      }
    }

    // Add ONLY the truthful recovery markers the consumer built as native line-item properties.
    // Idempotent per key (a re-offer never duplicates a field) and tagged so a late-settling
    // release can strip them again.
    function injectRecoveryProperties(controller, form, props) {
      if (!form || !props) return;
      if (!controller.fallbackFields) controller.fallbackFields = [];
      for (var key in props) {
        if (!hasOwnCall(props, key)) continue;
        var name = 'properties[' + key + ']';
        var existing = null;
        try {
          var found = form.querySelectorAll ? form.querySelectorAll('input[name="' + name + '"]') : null;
          if (found && found.length) existing = found[0];
        } catch (e) { existing = null; }
        var input = existing || document.createElement('input');
        var record = recoveryFieldOwners.get(input);
        if (!record || record.controller !== controller) {
          var previous = record;
          record = {
            controller: controller, input: input, previousOwner: previous,
            created: previous ? previous.created : !existing,
            type: previous ? previous.type : input.getAttribute('type'),
            valueAttr: previous ? previous.valueAttr : input.getAttribute('value'),
            value: previous ? previous.value : input.value,
            marker: previous ? previous.marker : input.getAttribute('data-pz-recovery-field')
          };
          controller.fallbackFields.push(record);
          recoveryFieldOwners.set(input, record);
        }
        record.ownedValue = String(props[key]);
        record.form = form;
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', name);
        input.setAttribute('value', String(props[key]));
        if ('value' in input) input.value = String(props[key]);
        input.setAttribute('data-pz-recovery-field', 'bootstrap');
        if (!existing) form.appendChild(input);
      }
    }

    // The body flag reveals SSR-gated ATC (inhouse-invalid ships display:none until armed) ONLY
    // while a fallback is live, so a no-JS / failed-bootstrap page stays fail-closed.
    function updateFallbackBodyClass() {
      if (!document.body || !document.body.classList) return;
      var any = false;
      controllers.forEach(function (c) { if (ownsLifecycle(c) && c.bootstrapFallback === true) any = true; });
      document.body.classList.toggle('pz-fallback-open', any);
    }

    // Undo an armed fallback when the real release finally loads (late settle): drop the injected
    // markers and the open flags so the normal runtime path owns the sale cleanly.
    function clearBootstrapFallback(controller) {
      if (!controller || !controller.bootstrapFallback) return;
      controller.bootstrapFallback = false;
      controller.fallbackForm = null;
      controller.commerceFallback = false;
      var fields = controller.fallbackFields || [];
      controller.fallbackFields = [];
      for (var i = 0; i < fields.length; i++) {
        var record = fields[i];
        var input = record.input;
        // Keep the original node/form, not a fresh lookup through a reusable form id. A newer
        // lifecycle can adopt a field; an old disposer must not remove that owner's markers.
        if (record.controller !== controller || recoveryFieldOwners.get(input) !== record) continue;
        // Either unload order is valid when two live mounts share a native form. Restore a
        // surviving fallback owner's values before releasing the newer owner's write-set.
        var previous = record.previousOwner;
        while (previous && (!ownsLifecycle(previous.controller) || !previous.controller.bootstrapFallback ||
          previous.controller.fallbackForm !== previous.form || !previous.form.contains(input))) previous = previous.previousOwner;
        if (previous) {
          recoveryFieldOwners.set(input, previous);
          input.setAttribute('value', previous.ownedValue);
          input.value = previous.ownedValue;
          continue;
        }
        recoveryFieldOwners.delete(input);
        if (record.created) {
          if (input.parentNode) input.parentNode.removeChild(input);
        } else {
          restoreAttribute(input, 'type', record.type);
          restoreAttribute(input, 'value', record.valueAttr);
          input.value = record.value;
          restoreAttribute(input, 'data-pz-recovery-field', record.marker);
        }
      }
      if (controller.mount) {
        controller.mount.removeAttribute('data-pz-runtime-error-code');
        if (!controller.fallbackHadErrorClass) controller.mount.classList.remove('pz-mount--error');
      }
      updateFallbackBodyClass();
    }

    function restoreAttribute(node, name, value) {
      if (value == null) node.removeAttribute(name);
      else node.setAttribute(name, value);
    }

    function retryBootstrapFailures() {
      controllers.forEach(function (controller) {
        if (!controller.bootstrapFailure) return;
        var form = nativeFormFor(controller);
        if (controller.bootstrapFallback && form !== controller.fallbackForm) {
          clearBootstrapFallback(controller);
          recomputeAtc();
        }
        if (!controller.bootstrapFallback && form) fireFail(controller, controller.bootstrapFailure);
      });
    }

    // Offer a pre-frame failure to the registered consumers (the critical session). Returns true
    // iff a consumer opened the truthful commerce fallback.
    function fireFail(controller, code) {
      if (!ownsLifecycle(controller)) return false;
      var detail = {
        mount: controller.mount, controller: controller, sectionId: controller.sectionId,
        controllerId: controller.controllerId, code: code
      };
      for (var i = 0; i < failCallbacks.length; i++) {
        if (!ownsLifecycle(controller)) return false;
        try { failCallbacks[i](detail); }
        catch (e) { log('fail-callback-failed', {}); }
        // A callback's return value is not evidence that the purchase gate was released.
        if (ownsLifecycle(controller) && controller.bootstrapFallback && controller.commerceFallback) return true;
      }
      return false;
    }

    // Sale-first: a pre-frame failure offers the truthful fallback first; only if no consumer
    // opens it (old theme, edit/restore context, or no native form yet) does it fail closed.
    function failOrFallback(controller, code, extra, reason) {
      if (!ownsLifecycle(controller)) return;
      if (!controller.bootstrapFailure) {
        controller.fallbackHadErrorClass = controller.mount.classList.contains('pz-mount--error');
      }
      controller.bootstrapFailure = code;
      // Preserve a truthful render ERROR, including when the consumer is not installed yet.
      // Only this recorded pre-release failure is replayable; runtime refusals are not.
      if (!controller.bootstrapFallback) failClosed(controller, code, extra, reason);
      else clearFallbackTimer(controller);
      fireFail(controller, code);
    }

    function markReady(controller) {
      if (!ownsLifecycle(controller)) return;
      // A late-settling release (fetch that hung past the fallback budget, then arrived) re-arms
      // the normal lifecycle: drop the injected recovery markers so no stale marker rides a design
      // that actually rendered.
      clearFallbackTimer(controller);
      clearBootstrapFallback(controller);
      controller.release = controller.pendingRelease;
      controller.bootstrapFailure = null;
      if (controller.mount) controller.mount.removeAttribute('data-pz-fail-reason');
      setState(controller, STATE.READY);
      recomputeAtc(); // a now-valid section may release the block
      if (controller.fallbackStatus && !document.body.classList.contains('pz-fallback-open')) {
        controller.fallbackStatus.textContent = 'Personalization is loading. Add to cart is unavailable until your design is ready.';
      }
      controller.fallbackStatus = null;
      // NB: READY is NOT T2. The renderer marks T2 (markAssetsReady) when
      // selected assets/font/controls are ready, and T3 on committed frame.
      notifyReady(controller);
    }

    function fetchRelease(controller) {
      var url = controller.releaseUrl;
      var origins = controller.allowedOrigins;
      if (!isAllowedReleaseUrl(url, origins)) {
        failOrFallback(controller, 'invalid-release-url', { host: safeHost(url) });
        return;
      }
      setState(controller, STATE.LOADING);
      var fetchStartedAt = Date.now();
      // Never-settle guard: a fetch that neither resolves nor rejects would keep the controller
      // LOADING forever and hard-block Add to Cart. After the budget, a still-LOADING owned
      // controller takes the same pre-frame fallback as an outright failure.
      if (typeof root.setTimeout === 'function') {
        controller.fallbackTimer = root.setTimeout(function () {
          controller.fallbackTimer = null;
          if (!controller.disposed && ownsLifecycle(controller) && controller.state === STATE.LOADING) {
            var reason = document.visibilityState === 'hidden' ? 'release-timeout-hidden'
              : Date.now() - fetchStartedAt > 12000 ? 'release-timeout-late' : null;
            hadLoss = true;
            trace('timeout', 'release-timeout', 'timeout');
            failOrFallback(controller, 'release-timeout', {}, reason);
            observe(); // diagnostics AFTER the sale-first fallback
          }
        }, DEFAULT_RELEASE_FALLBACK_BUDGET_MS);
      }
      var maxBytes = config.maxDecodedReleaseBytes;
      var delays = [300, 1200], retryTimer = null, cancelBack = null;
      var attempt = 0, retryIndex = 0, backLeft = 3, pending = false, stopped = false, hadLoss = false;
      var phase = 'req', status = 0, attemptStartedAt = fetchStartedAt;
      // PLAN-A 0352: technical attempt history for the head observer. Kept in this closure and handed out
      // only as a copied snapshot in the pz:release-fetch event detail — never on the mount, window or DOM.
      var attemptLog = [], attemptStartedPerf = 0, cfRay = null, lastTrace = null;
      // ResourceTiming lives in the realm that performed the fetch (the clean frame), not the page window.
      var fetchPerf = (typeof clean !== 'undefined' && clean && clean.performance) || performance;
      function perfNow() { try { return fetchPerf.now(); } catch (e) { return 0; } }
      function timing(start) {
        try {
          var list = fetchPerf.getEntriesByName(url), e = null, i;
          for (i = 0; i < list.length; i++) if (list[i].startTime >= start - 1) e = list[i];
          if (!e) return { present: false };
          var gap = function (to, from) { return to > 0 && from > 0 ? Math.max(0, Math.round(to - from)) : null; };
          return { present: true, dns: gap(e.domainLookupEnd, e.domainLookupStart), connect: gap(e.connectEnd, e.connectStart),
            tls: gap(e.connectEnd, e.secureConnectionStart), ttfb: gap(e.responseStart, e.requestStart),
            download: gap(e.responseEnd, e.responseStart), transfer: e.transferSize, encodedBodySize: e.encodedBodySize,
            decodedBodySize: e.decodedBodySize, protocol: e.nextHopProtocol || null, deliveryType: e.deliveryType };
        } catch (x) { return { present: false }; }
      }
      function current() { return !stopped && ownsLifecycle(controller); }
      function clearBack() { if (cancelBack) { var off = cancelBack; cancelBack = null; off(); } }
      function cancelRecovery() {
        stopped = true;
        if (retryTimer !== null) { root.clearTimeout(retryTimer); retryTimer = null; }
        clearBack();
      }
      controller.cancelReleaseRecovery = cancelRecovery;
      function trace(outcome, reason, errorKind) {
        if (!current()) return;
        var entry = {
          attempt: attempt, phase: phase, status: status, outcome: outcome, reason: preReleaseFailReason(reason),
          errorKind: errorKind || null, durationMs: Math.max(0, Date.now() - attemptStartedAt),
          cache: attempt === 1 ? 'force-cache' : 'reload', cfRay: cfRay,
          startedAt: attemptStartedAt, rt: outcome === 'pending' ? null : timing(attemptStartedPerf)
        };
        // One row per attempt number (a late settle after the 8 s timeout updates the same row).
        if (attempt >= 1 && attempt <= 8) attemptLog[attempt - 1] = entry;
        lastTrace = entry;
      }
      function snapshot() {
        var rows = [];
        for (var i = 0; i < attemptLog.length; i++) {
          var r = attemptLog[i];
          if (r) rows.push({ attempt: r.attempt, phase: r.phase, status: r.status, outcome: r.outcome, reason: r.reason,
            errorKind: r.errorKind, durationMs: r.durationMs, cache: r.cache, cfRay: r.cfRay, startedAt: r.startedAt, rt: r.rt });
        }
        var t = lastTrace || {};
        return { attempt: t.attempt, phase: t.phase, status: t.status, outcome: t.outcome, reason: t.reason,
          durationMs: t.durationMs, total: attempt, url: url, fallbackOpen: !!controller.bootstrapFallback, attempts: rows };
      }
      // The head observer serializes only bounded technical fields. No error message, URL or
      // shopper values cross this seam. Commerce is settled BEFORE this best-effort observer.
      function observe() {
        if (!ownsLifecycle(controller)) return;
        try { root.dispatchEvent(new root.CustomEvent('pz:release-fetch', {
          detail: { mount: controller.mount, controllerId: controller.controllerId, trace: snapshot() }
        })); } catch (e) { /* diagnostics never gate release/commerce */ }
      }
      function armBack() {
        // Same R3 sale-open policy: after the ladder, at most three fresh cycles on real
        // browser signals, only while foreground+online. No timer loop and no budget reset.
        if (!current() || !backLeft || cancelBack || !controller.bootstrapFallback
          || !controller.commerceFallback || typeof root.addEventListener !== 'function') return;
        var fire = function () {
          if (!current() || pending || document.visibilityState === 'hidden'
            || (root.navigator && root.navigator.onLine === false)) return;
          clearBack(); backLeft--; retryIndex = 0; run();
        };
        root.addEventListener('online', fire);
        root.addEventListener('pageshow', fire);
        document.addEventListener('visibilitychange', fire);
        cancelBack = function () {
          root.removeEventListener('online', fire);
          root.removeEventListener('pageshow', fire);
          document.removeEventListener('visibilitychange', fire);
        };
      }
      function fail(code, extra, retriable, errorKind) {
        if (!current()) return;
        pending = false;
        hadLoss = true;
        trace('failed', code, errorKind || 'unknown');
        // Preserve LIVE sale-first timing: never hold fallback for 300/1200ms retries.
        failOrFallback(controller, code, extra);
        if (!current()) return; // onFail may synchronously supersede/dispose this lifecycle
        observe(); // every settled attempt, AFTER the fallback; the head decides what to send
        if (!current()) return;
        if (!retriable) { cancelRecovery(); return; }
        if (retryIndex < delays.length && typeof root.setTimeout === 'function') {
          var delay = delays[retryIndex++];
          retryTimer = root.setTimeout(function () {
            retryTimer = null;
            if (current()) run();
          }, delay);
        } else armBack();
      }
      function run() {
        if (!current() || pending) return;
        pending = true; attempt++; phase = 'req'; status = 0; attemptStartedAt = Date.now();
        attemptStartedPerf = perfNow(); cfRay = null;
        trace('pending');
        if (hadLoss) observe(); // a retry in flight: lets the head tell 'pending at pagehide' apart
        var finalUrl = url;
        releaseTransport(function () { return nativeFetch(url, {
          method: 'GET', mode: 'cors', credentials: 'same-origin', redirect: 'follow',
          // Initial fetch still matches anonymous preload. Only retry bypasses the failed cache.
          cache: attempt === 1 ? 'force-cache' : 'reload', signal: controller.abortSignal
        }); }, 'req').then(function (res) {
          if (!current()) return null;
          phase = 'check'; status = responseStatusCall(res);
          try { cfRay = headersGetCall(responseHeadersCall(res), 'cf-ray') || null; } catch (e) { cfRay = null; }
          if (responseOkCall(res) !== true) {
            fail('release-http-error', { status: status, host: safeHost(url) },
              status === 408 || status === 425 || status === 429 || status >= 500 && status <= 599, 'http');
            return null;
          }
          finalUrl = responseUrlCall(res) || url;
          if (finalUrl !== url && !isAllowedReleaseUrl(finalUrl, origins)) {
            fail('release-redirect-origin', { host: safeHost(finalUrl) }, false, 'unknown'); return null;
          }
          phase = 'body'; trace('pending');
          return readCapped(res, maxBytes);
        }).then(function (text) {
          if (text == null || !current()) return;
          phase = 'check';
          var release;
          try { release = jsonParse(text); }
          catch (e) { fail('release-parse-error', { host: safeHost(finalUrl) }, false, 'parse'); return; }
          if (!matchesReleaseContract(release, config.releaseContract)) {
            fail('release-contract-mismatch', { schemaName: release && release.schemaName,
              schemaVersion: release && release.schemaVersion }, false); return;
          }
          if (!assetsSameOrigin(release, finalUrl)) {
            fail('release-asset-origin', { host: safeHost(finalUrl) }, false); return;
          }
          pending = false; phase = 'ready'; trace('recovered');
          clearBack();
          controller.pendingRelease = release;
          markReady(controller);
          // READY means validated RELEASE handed off, not a committed preview or successful ATC.
          if (hadLoss && current() && controller.state === STATE.READY) observe();
          cancelRecovery();
        }).catch(function (err) {
          if (!current()) return;
          if (err && err.name === 'AbortError') { cancelRecovery(); return; }
          if (err && err.releasePhase) phase = err.releasePhase;
          var code = err && err.code ? err.code : 'release-fetch-failed';
          fail(code, { host: safeHost(url) }, !!(err && err.releaseTransport),
            err && err.releaseTransport ? 'network_or_cors' : 'unknown');
        });
      }
      run();
    }

    function notifyReady(controller) {
      var detail = {
        mount: controller.mount,
        release: controller.release,
        controller: controller,
        sectionId: controller.sectionId,
        controllerId: controller.controllerId
      };
      for (var i = 0; i < readyCallbacks.length; i++) {
        // A callback may synchronously refresh and supersede this generation. Never fan a stale
        // ready detail out to the remaining callbacks after exact lifecycle ownership changed.
        if (!ownsLifecycle(controller)) return;
        try { readyCallbacks[i](detail); } catch (e) { log('ready-callback-threw', {}); }
      }
    }

    function disposeController(controller) {
      if (!controller || controller.disposed) return;
      // Only the current owner may clear this mount's diagnostic during teardown.
      if (ownsLifecycle(controller) && controller.mount) controller.mount.removeAttribute('data-pz-fail-reason');
      controller.disposed = true;
      if (controller.cancelReleaseRecovery) controller.cancelReleaseRecovery();
      controller.bootstrapFailure = null;
      clearBootstrapFallback(controller);
      clearFallbackTimer(controller);
      if (controller.abort) { try { controller.abort.abort(); } catch (e) {} }
    }

    function initMount(mount) {
      if (mount.__pzBootstrapped) return;
      mount.__pzBootstrapped = true;

      mark('pz:t0'); // static shell (poster/price/form) already painted

      var identity = mountIdentityOf(mount);
      var sectionId = identity.sectionId;
      var controllerId = identity.controllerId;
      var provider = mount.getAttribute('data-pz-provider');
      var attrOrigins = parseAllowedOrigins(mount.getAttribute('data-pz-allowed-origins'));
      // Construct the abort handle before claiming the reservation. Even a generation that
      // loses during synchronous superseded-controller disposal is therefore disposed/aborted
      // exactly once, while still never starting a fetch.
      var handle = makeAbortHandle(root.AbortController);
      var reservation = {};
      var controller = {
        mount: mount,
        // Public/external grouping identity remains raw for a real Shopify section. Internal
        // renderer calls receive this controller object through a bound storefront bridge.
        sectionId: identity.kind === 'section' ? sectionId : controllerId,
        externalSectionId: sectionId,
        controllerId: controllerId,
        identityKind: identity.kind,
        provider: provider,
        releaseUrl: mount.getAttribute('data-pz-release-url') || '',
        allowedOrigins: attrOrigins.length ? attrOrigins : config.allowedOrigins,
        variantId: mount.getAttribute('data-pz-variant-id') || '',
        variantIngressSeq: 0,
        state: STATE.IDLE,
        release: null,
        pendingRelease: null,
        assetsReady: false,
        visualFrameCommitted: false,
        frameCommitted: false,
        commerceFallback: false,
        purchaseValidator: null,
        abort: handle.controller,
        abortSignal: handle.signal,
        disposed: false,
        reservation: reservation
      };
      // Claim first. Any refresh synchronously entered by disposeController() below installs a
      // different opaque reservation. The stale outer stack can then observe that it lost and
      // must never overwrite the nested winner or start its own fetch/runtime handoff.
      reservations.set(controllerId, reservation);
      var superseded = controllers.get(controllerId);
      // A Theme Editor replacement may arrive before its old markup detaches. Never silently
      // overwrite a live Map entry: stale fetch completion from the superseded mount must not
      // emit a second ready handoff for the same typed identity. Remove it BEFORE aborting so a
      // synchronous/re-entrant unload callback cannot delete the replacement installed below.
      if (superseded && superseded.mount !== mount) {
        controllers.delete(controllerId);
        disposeController(superseded);
      }

      if (reservations.get(controllerId) !== reservation) {
        disposeController(controller);
        recomputeAtc();
        return;
      }
      controllers.set(controllerId, controller);

      mark('pz:t1'); // personalizer shell present, release fetch about to start

      // Block ATC synchronously from init: an inhouse controller cannot enable
      // purchase until it commits a frame (T3), regardless of load outcome.
      recomputeAtc();

      if (provider === 'inhouse-invalid') {
        // Metadata is invalid so there is no release to render, but purchase must not stay
        // locked once the native form is ready: offer the truthful pre-frame fallback, else
        // fail closed exactly as before.
        failOrFallback(controller, 'inhouse-invalid-metadata', {});
        return;
      }
      fetchRelease(controller);
    }

    function bootstrapAll() {
      var mounts = document.querySelectorAll('[data-pz-personalizer]');
      for (var i = 0; i < mounts.length; i++) initMount(mounts[i]);
      retryBootstrapFailures();
    }

    function teardownController(candidate) {
      var controller = candidate && candidate.controller;
      if (!controller) return false;
      var controllerId = candidate.controllerId;
      var reservation = candidate.reservation;
      // Resolve nothing from a reusable key here. Every caller must retain the exact object and
      // reservation it observed; synchronous abort/re-entry can then replace either owner and
      // turn a stale teardown into a no-op instead of deleting the newer generation.
      if (controller.controllerId !== controllerId || controller.reservation !== reservation ||
          controllers.get(controllerId) !== controller ||
          reservations.get(controllerId) !== reservation) return false;
      // Delete exact ownership before aborting. Abort callbacks may synchronously install a new
      // generation under the same key, which must remain reachable and keep its reservation.
      if (controller.mount) controller.mount.removeAttribute('data-pz-fail-reason');
      controllers.delete(controllerId);
      reservations.delete(controllerId);
      disposeController(controller); // stale-guard for in-flight fetch (esp. no AbortController)
      recomputeAtc(); // removing an error section may release the block
      return true;
    }

    function snapshotController(controller, unloadTarget) {
      return {
        controller: controller,
        controllerId: controller.controllerId,
        reservation: controller.reservation,
        mount: controller.mount,
        unloadTarget: unloadTarget || null
      };
    }

    function mountInUnloadTarget(target, mount) {
      if (!target || target === document || !mount) return false;
      if (target === mount) return true;
      if (typeof target.contains !== 'function') return false;
      try { return target.contains(mount); } catch (e) { return false; }
    }

    function teardownShopifySection(event) {
      var candidates = [];
      var detail = event && event.detail;
      var externalId = String(detail && detail.sectionId != null ? detail.sectionId : '').trim();

      // Raw Shopify ids own only the explicit typed section namespace. Public text equality is
      // never authority to cross into DOM/generated identities.
      if (externalId) {
        var sectionController = controllers.get(typedControllerIdentity('section', externalId));
        if (sectionController) candidates.push(snapshotController(sectionController));
      }

      // A non-section controller may still be owned by the unloading section when its exact mount
      // is demonstrably inside the event target subtree. `document` itself is not section
      // ownership: accepting it would make a document-dispatched raw event capture every mount.
      var target = event && event.target;
      if (target && target !== document) {
        controllers.forEach(function (controller) {
          if (!mountInUnloadTarget(target, controller.mount)) return;
          for (var i = 0; i < candidates.length; i++) {
            if (candidates[i].controller === controller) return;
          }
          candidates.push(snapshotController(controller, target));
        });
      }

      for (var i = 0; i < candidates.length; i++) {
        var candidate = candidates[i];
        if (candidate.unloadTarget &&
            (candidate.controller.mount !== candidate.mount ||
             !mountInUnloadTarget(candidate.unloadTarget, candidate.mount))) continue;
        teardownController(candidate);
      }
    }

    function controllerFor(reference) {
      if (!reference) return null;
      if (typeof reference === 'object') {
        if (reference.controllerId) return ownsLifecycle(reference) ? reference : null;
        try {
          var mountKey = controllerIdentityOf(reference);
          var mountedController = mountKey && controllers.get(mountKey);
          return mountedController && mountedController.mount === reference ? mountedController : null;
        } catch (e) { return null; }
      }

      var identity = String(reference).trim();
      if (!identity) return null;
      // Exact internal typed keys have deterministic precedence. A raw Shopify id that happens
      // to spell that key remains owned by unload/external grouping and must use object identity
      // for internal marks; an ambiguous caller string can never capture the typed lifecycle.
      if (controllers.has(identity)) return controllers.get(identity);
      var explicitSectionKey = typedControllerIdentity('section', identity);
      if (controllers.has(explicitSectionKey)) return controllers.get(explicitSectionKey);

      var found = null;
      var count = 0;
      controllers.forEach(function (controller) {
        if (controller.externalSectionId === identity) { found = controller; count++; }
      });
      return count === 1 ? found : null; // duplicate cross-namespace public ids fail closed
    }

    // Re-arm a controller: the committed frame is stale for the new selection,
    // so drop assetsReady/frameCommitted and block ATC synchronously until a new
    // T3. Used by variant changes and by the public markFramePending().
    function markPending(controller) {
      controller.assetsReady = false;
      controller.visualFrameCommitted = false;
      controller.frameCommitted = false;
      // Pre-release fallback has no frame to rebuild. Keep sale-open on variant changes;
      // native variant id and availability remain Shopify-owned. READY/T3 is unchanged.
      controller.commerceFallback = controller.bootstrapFallback === true;
      if (controller.mount) {
        controller.mount.removeAttribute('data-pz-assets-ready');
        controller.mount.classList.remove('is-frame-ready');
      }
    }

    function ownedShopifyVariantId(controller) {
      var mount = controller && controller.mount;
      if (!mount || !document.getElementById) return '';
      var formId = String(mount.getAttribute('data-pz-form-id') || '').trim();
      if (!formId) return '';
      var form = document.getElementById(formId);
      if (!form || typeof form.querySelectorAll !== 'function') return '';
      var inputs = form.querySelectorAll('input[name="id"]');
      if (!inputs || inputs.length !== 1) return '';
      var value = String(inputs[0].value || '').trim();
      // Keep Shopify's opaque identifier as a string. A bounded positive-decimal
      // shape rejects option labels/empty values without losing 64-bit precision.
      return /^[1-9][0-9]{0,19}$/.test(value) ? value : '';
    }

    function scheduleVariantIngress(controller) {
      var seq = ++controller.variantIngressSeq;
      // Shrine can update the product form's hidden variant id in a microtask after this
      // capture listener returns. Cross the task boundary so every already-queued microtask
      // settles before reading Shopify's authoritative id; otherwise the personalizer trails
      // the selected brand by exactly one change.
      var defer = typeof root.setTimeout === 'function' ? root.setTimeout.bind(root) : setTimeout;
      defer(function () {
        if (!ownsLifecycle(controller) || controller.variantIngressSeq !== seq) return;
        // Reconcile again after Shopify's own change handlers/microtasks replace the form.
        retryBootstrapFailures();
        if (!ownsLifecycle(controller) || controller.variantIngressSeq !== seq) return;
        var variantId = ownedShopifyVariantId(controller);
        if (!variantId) {
          log('variant-id-unresolved', { sectionId: controller.externalSectionId });
          return; // pending state remains fail-closed until a valid later change
        }
        controller.variantId = variantId;
        controller.mount.setAttribute('data-pz-variant-id', variantId);
      }, 0);
    }

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.name !== 'id' && !t.closest('variant-selects, variant-radios, variant-inputs')) return;
      var changed = false;
      controllers.forEach(function (controller) {
        var section = controller.mount && controller.mount.closest('.shopify-section, section');
        if (section && section.contains(t)) {
          if (loadingIntent && loadingIntent.controller === controller) clearLoadingIntent();
          markPending(controller); // stale committed frame -> re-block
          // Shrine handles the same change synchronously after document capture,
          // resolves the selected product variant and updates the owned hidden
          // product-form input. Never relay the display option value (e.g.
          // "Samsung"); read the authoritative Shopify id after propagation.
          scheduleVariantIngress(controller);
          changed = true;
        }
      });
      if (changed) {
        retryBootstrapFailures();
        recomputeAtc(); // healthy frames still re-block synchronously on variant change
      }
    }, true);

    document.addEventListener('shopify:section:load', bootstrapAll);
    var detachedCleanupScheduled = false;
    function cleanupDetachedControllers() {
      var detached = [];
      controllers.forEach(function (controller) {
        if (controller.mount && !document.contains(controller.mount)) detached.push(snapshotController(controller));
      });
      for (var i = 0; i < detached.length; i++) {
        var candidate = detached[i];
        // Revalidate the exact mount state immediately before exact-owner teardown. An earlier
        // abort may synchronously install a connected replacement under this candidate's key.
        if (candidate.controller.mount !== candidate.mount ||
            !candidate.mount || document.contains(candidate.mount)) continue;
        teardownController(candidate);
      }
    }
    function scheduleDetachedCleanup() {
      if (detachedCleanupScheduled) return;
      detachedCleanupScheduled = true;
      root.Promise.resolve().then(function () {
        detachedCleanupScheduled = false;
        cleanupDetachedControllers();
      });
    }
    document.addEventListener('shopify:section:unload', function (e) {
      teardownShopifySection(e);
      cleanupDetachedControllers();
      // Shopify dispatches unload before removing markup. Recheck after this event stack so a
      // non-section fallback with no raw unload identity cannot leave a controller/ticket behind.
      scheduleDetachedCleanup();
    });

    root.addEventListener('pageshow', function (e) {
      if (e.persisted) recomputeAtc();
    });

    // ---- public API (renderer handoff) ----
    api.onReady = function (cb) {
      if (typeof cb !== 'function') return;
      readyCallbacks.push(cb);
      controllers.forEach(function (controller) {
        if (controller.state === STATE.READY) cb({
          mount: controller.mount,
          release: controller.release,
          controller: controller,
          sectionId: controller.sectionId,
          controllerId: controller.controllerId
        });
      });
    };
    // F1 sale-open: a consumer (the critical session) registers here to open the truthful
    // commerce fallback on a pre-frame failure. It returns true iff it opened commerce; see
    // failOrFallback. No consumer => the exact fail-closed behavior is unchanged.
    api.onFail = function (cb) {
      if (typeof cb !== 'function') return;
      failCallbacks.push(cb);
      retryBootstrapFailures();
    };
    api.getController = controllerFor;
    api._reservationCount = function () { return reservations.size; };
    // T2: only when the controller exists and is READY.
    api.markAssetsReady = function (reference) {
      var controller = controllerFor(reference);
      if (!canMarkAssetsReady(controller)) return false;
      controller.assetsReady = true;
      if (controller.mount) controller.mount.setAttribute('data-pz-assets-ready', 'true');
      mark('pz:t2');
      return true;
    };
    // Reveal a current frame to the native gallery and Preview without claiming purchase T3.
    // A required phone model may still be unconfirmed, so ATC remains fail-closed.
    api.markVisualFrameCommitted = function (reference, frameSignal) {
      var controller = controllerFor(reference);
      if (!canMarkFrameCommitted(controller, frameSignal)) return false;
      if (!controller.visualFrameCommitted) mark('pz:visual');
      controller.visualFrameCommitted = true;
      if (controller.mount) {
        controller.mount.classList.add('is-frame-ready');
        controller.mount.classList.add('pz-has-committed-frame');
      }
      resumeLoadingPurchase();
      return true;
    };
    // T3: only after T2 (assetsReady) AND a real committed-frame signal. This is
    // the ONLY thing that unblocks ATC for a valid inhouse controller.
    api.markFrameCommitted = function (reference, frameSignal) {
      var controller = controllerFor(reference);
      if (!canMarkFrameCommitted(controller, frameSignal)) return false;
      if (!controller.visualFrameCommitted) mark('pz:visual');
      controller.visualFrameCommitted = true;
      controller.frameCommitted = true;
      controller.commerceFallback = false;
      mark('pz:t3');
      if (controller.mount) {
        controller.mount.classList.add('is-frame-ready');
        controller.mount.classList.add('pz-has-committed-frame');
      }
      recomputeAtc(); // may unblock now that this section committed a frame
      return true;
    };
    // Exact-controller validation seam for blocked native ATC clicks. It can surface/focus
    // missing shopper input but cannot change lifecycle state or submit commerce.
    api.setPurchaseValidator = function (reference, validator) {
      var controller = controllerFor(reference);
      if (!controller || typeof validator !== 'function') return null;
      controller.purchaseValidator = validator;
      return function () {
        // Bootstrap unload can release ownership before the storefront binding disposes.
        // Exact function identity makes clearing the retained stale object safe and prevents
        // an old disposer from touching a replacement lifecycle.
        if (controller.purchaseValidator === validator) {
          controller.purchaseValidator = null;
        }
      };
    };
    // A typed storefront/runtime refusal after release validation is still a terminal provider
    // error. Promote the exact bootstrap lifecycle to ERROR so purchase stays both technically
    // and visibly hard-disabled; stale or ambiguous references cannot poison another section.
    api.markError = function (reference, runtimeCode) {
      var controller = controllerFor(reference);
      if (!controller || !ownsLifecycle(controller)) return false;
      var safe = publicErrorCode(runtimeCode);
      if (controller.cancelReleaseRecovery) controller.cancelReleaseRecovery();
      controller.bootstrapFailure = null;
      // The bootstrap never calls markError itself. A missing external code is a runtime error,
      // not a made-up release reason; any supplied unallowlisted code remains unknown.
      var reason = runtimeCode === undefined ? 'runtime-error'
        : typeof runtimeCode === 'string' &&
          (runtimeCode === 'E_CRITICAL_UNKNOWN' || PUBLIC_ERROR_CODE_LOOKUP[runtimeCode] === true)
          ? runtimeCode : 'unknown';
      failClosed(controller, 'storefront-runtime-refused', { runtimeCode: safe }, reason);
      if (controller.mount) controller.mount.setAttribute('data-pz-runtime-error-code', safe);
      return true;
    };
    // Runtime selection change: re-arm + block synchronously until a new T3.
    api.markFramePending = function (reference) {
      var controller = controllerFor(reference);
      if (!controller) return false;
      markPending(controller);
      recomputeAtc();
      return true;
    };
    // Weak device / slow network (owner 02/09, sale-first): after a transient render transport
    // loss the runtime may sell on truthful line properties without a frame (manual print later).
    // Only a READY controller with its exact purchase validator installed qualifies; ERROR,
    // unrelated pending controllers and stale references stay blocked. Never claims T2/T3.
    api.markCommerceFallback = function (reference) {
      var controller = controllerFor(reference);
      if (!controller || !ownsLifecycle(controller) || controller.state !== STATE.READY
        || typeof controller.purchaseValidator !== 'function') return false;
      controller.commerceFallback = true;
      recomputeAtc();
      // Calm, truthful status for assistive tech (the element is visually hidden): purchase is open,
      // the design is prepared from the answers — never the loading/unavailable wording.
      var status = ensureStatusEl();
      if (status) status.textContent = 'Preview could not load on this connection. You can add to cart now; we will prepare your design from your answers.';
      return true;
    };
    // F1 sale-open (owner GO 2026-09-05): open commerce for a PRE-FRAME bootstrap failure once the
    // native product form exists — WITHOUT faking a frame or clearing the render ERROR. Only a
    // recorded bootstrap failure can arm this exception to the existing ATC predicate; runtime
    // refusals remain fail-closed. The consumer supplies the truthful marker-only recovery
    // properties (needs-recovery + unique operation id, no state id, no answers); here we stamp
    // them on the native form, record the reason as a diagnostic (never a cart property), and
    // reveal any SSR-gated ATC. A READY / committed / stale controller keeps the fail-closed path,
    // so this never disturbs a healthy design or the READY M1-lite runtime fallback.
    api.markStorefrontFallback = function (reference, code, props) {
      var controller = controllerFor(reference);
      if (!controller || !ownsLifecycle(controller) || !controller.bootstrapFailure
        || controller.state === STATE.READY || controller.frameCommitted) return false;
      var form = nativeFormFor(controller);
      if (!form) return false;
      clearFallbackTimer(controller);
      injectRecoveryProperties(controller, form, props);
      controller.commerceFallback = true;
      controller.bootstrapFallback = true;
      controller.fallbackForm = form;
      if (controller.mount) {
        controller.mount.classList.add('pz-mount--error');
        controller.mount.setAttribute('data-pz-runtime-error-code', publicErrorCode(fallbackDiagCode(code)));
      }
      updateFallbackBodyClass();
      recomputeAtc();
      log('E_STOREFRONT_FALLBACK_SALE', { code: code });
      var status = ensureStatusEl();
      if (status) status.textContent = 'Live preview is temporarily unavailable. You can still add this item to cart; our team will follow up to finalize your personalization.';
      controller.fallbackStatus = status;
      return true;
    };
    api.refresh = bootstrapAll;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootstrapAll);
    } else {
      bootstrapAll();
    }
  };

  return api;
});
